import { nextTick, ref, Ref } from "vue";
import { KeenSliderInstance } from "keen-slider";
import { DsButtonRefType } from "@sazka/design-system/src/button/ds-button-reference";
import { getSliderMaxIdx } from "../keen-slider-utils";

export default function useCustomNavigationPlugin(
    arrowLeft: Ref<DsButtonRefType>,
    arrowRight: Ref<DsButtonRefType>,
    dots: Ref<HTMLElement>,
    useVueHooks = false
) {
    if (!arrowLeft) arrowLeft = ref(null);
    if (!arrowRight) arrowRight = ref(null);

    const keenCustomNavigationPlugin = (slider: KeenSliderInstance) => {
        const sliderPrev = () => slider.prev();
        const sliderNext = () => slider.next();

        let isLooping = slider.options.loop;

        const bindButtons = () => {
            arrowLeft?.value?.addEventListener("click", sliderPrev);
            arrowRight?.value?.addEventListener("click", sliderNext);
        };

        const createDot = (idx: number) => {
            const dot = document.createElement("li");
            dot.classList.add("dot");
            dot.setAttribute("aria-label", `Go to slide ${idx + 1}`);
            dot.setAttribute("aria-controls", `slide${idx + 1}`);
            dot.setAttribute("role", "tab");
            dot.setAttribute("aria-selected", "false");
            dot.addEventListener("click", (e: Event) => {
                e.preventDefault();
                slider.moveToIdx(idx);
            });
            dots?.value?.appendChild(dot);
        };

        const createDots = () => {
            const maxIdx = getSliderMaxIdx(slider);
            if (!maxIdx || !dots?.value) {
                return;
            }

            [...Array(maxIdx + 1).keys()].forEach((idx) => createDot(idx));
        };

        const removeDots = () => {
            [...(dots?.value?.children || [])].forEach((dot) => dot.remove());
        };

        const initNavigations = () => {
            removeDots();
            createDots();
            bindButtons();
        };

        const removeEvents = () => {
            removeDots();
            arrowLeft.value?.removeEventListener("click", sliderPrev);
            arrowRight.value?.removeEventListener("click", sliderNext);
        };

        const updateClasses = () => {
            const { abs, maxIdx, rel } = slider.track.details;

            if (dots?.value?.children.length) {
                Array.from(dots?.value?.children).forEach(function (dot, idx) {
                    idx === rel
                        ? (dot.setAttribute("aria-selected", "true"), dot.classList.add("is-selected"))
                        : (dot.setAttribute("aria-selected", "false"), dot.classList.remove("is-selected"));
                });
            }

            if (isLooping) return;

            if (abs === 0) {
                arrowLeft?.value?.classList.add("button--disabled");
                arrowLeft?.value?.setAttribute("disabled", "");
            } else {
                arrowLeft?.value?.classList.remove("button--disabled");
                arrowLeft?.value?.removeAttribute("disabled");
            }

            if (abs === maxIdx) {
                arrowRight?.value?.classList.add("button--disabled");
                arrowRight?.value?.setAttribute("disabled", "");
            } else {
                arrowRight?.value?.classList.remove("button--disabled");
                arrowRight?.value?.removeAttribute("disabled");
            }
        };

        slider.on("created", () => {
            if (useVueHooks) {
                nextTick(() => {
                    initNavigations();
                    updateClasses();
                });
            } else {
                initNavigations();
                updateClasses();
            }
        });

        slider.on("optionsChanged", (slider) => {
            isLooping = slider.options.loop;

            removeDots();
            createDots();
            updateClasses();
        });

        slider.on("slideChanged", () => {
            updateClasses();
        });

        slider.on("updated", () => {
            removeDots();
            createDots();
            updateClasses();
        });

        slider.on("destroyed", () => {
            removeEvents();
        });
    };

    return keenCustomNavigationPlugin;
}
